import * as Yup from "yup" // Import Yup for schema validation
import _ from "lodash" // Import lodash for utility functions
import { LogError } from "../../helpers/logger"

// Regular expression to validate phone numbers
const phoneRegex = /^(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/

// Main function to generate conditional phone validation schema
export default function validation_conditional_phone(field, acc) {
  const { field_name, field_validation } = field // Destructure field name and validation rules

  // Get the conditional field name(s) from the parsed conditions
  const conditions = JSON.parse(field.field_conditional)
  const fieldConditional = _.keys(conditions)
  const conditionFieldName = fieldConditional[0]

  // Helper function to check if the field is required
  const checkBool = () => {
    if (!field_validation.required) return false
    return field_validation.required[0] === "true" ? true : false
  }

  const isRequired = checkBool() // Determine if the field is required
  const isRequiredTxt = field_validation.required[1] || "Required field." // Required field error message

  // Get min length and error message
  const min = (field_validation.min && Number(field_validation.min[0])) || null
  const minTxt =
    (field_validation.min && field_validation.min[1]) ||
    `Phone number must have at least ${min} characters.`

  // Get max length and error message
  const max = (field_validation.max && Number(field_validation.max[0])) || null
  const maxTxt =
    (field_validation.max && field_validation.max[1]) ||
    `Phone number must be less than ${max} characters long.`

  // Get phone pattern error message
  const phoneTxt =
    (field_validation.pattern && field_validation.pattern[1]) ||
    "Invalid phone number format."

  // If not required, return accumulator as is
  if (!isRequired) return acc

  // Special case: if the conditional field disables the phone field, make it nullable
  if (conditionFieldName === "participant_phone_primary_none") {
    return {
      [field_name]: Yup.string().nullable(),
      ...acc,
    }
  }

  // If both min and max are set, apply both validations
  if (min && max) {
    return {
      ...acc,
      [field_name]: Yup.string()
        .nullable()
        .when(conditionFieldName, (value, schema) => {
          const matches = _.isArray(value)
            ? _.some(value, (v) => _.includes(conditions[conditionFieldName], v))
            : _.includes(conditions[conditionFieldName], value)

          if (!matches) return schema.nullable()

          return schema
            .matches(phoneRegex, phoneTxt)
            .required(isRequiredTxt)
            .min(min, minTxt)
            .max(max, maxTxt)
        }),
    }
  } else if (min) {
    return {
      ...acc,
      [field_name]: Yup.string()
        .nullable()
        .when(conditionFieldName, (value, schema) => {
          const matches = _.isArray(value)
            ? _.some(value, (v) => _.includes(conditions[conditionFieldName], v))
            : _.includes(conditions[conditionFieldName], value)

          if (!matches) return schema.nullable()

          return schema
            .matches(phoneRegex, phoneTxt)
            .required(isRequiredTxt)
            .min(min, minTxt)
        }),
    }
  } else if (max) {
    return {
      ...acc,
      [field_name]: Yup.string()
        .nullable()
        .when(conditionFieldName, (value, schema) => {
          const matches = _.isArray(value)
            ? _.some(value, (v) => _.includes(conditions[conditionFieldName], v))
            : _.includes(conditions[conditionFieldName], value)

          if (!matches) return schema.nullable()

          return schema
            .matches(phoneRegex, phoneTxt)
            .required(isRequiredTxt)
            .max(max, maxTxt)
        }),
    }
  } else {
    return {
      ...acc,
      [field_name]: Yup.string()
        .nullable()
        .when(conditionFieldName, (value, schema) => {
          const conditionValues = conditions[conditionFieldName] || []
          if (!Array.isArray(conditionValues)) {
            LogError("Condition values is not an array:", conditionValues)
            return schema
          }

          const matches = Array.isArray(value)
            ? value.some((v) => conditionValues.includes(v))
            : conditionValues.includes(value)

          if (!matches) return schema.nullable()

          return schema
            .matches(phoneRegex, phoneTxt)
            .required(isRequiredTxt)
        }),
    }
  }
}